import { useCallback, useMemo } from 'react';
import { ViewApI } from 'api/views';
import { View, ViewType as ViewTypeEnum } from 'api/views/types';
import {
  created,
  deleted,
  deleting,
  reverted,
  undeleted,
  updated,
  updating,
} from './core';
import { ViewDefinitionTransformer, getViewTranformer } from './transformer';
import { CreatableView, Views } from './types';

interface Props<ViewType, UIViewDefinition, APIViewDefinition> {
  views: Views<UIViewDefinition>;
  setViews:
    | React.Dispatch<React.SetStateAction<Views<UIViewDefinition>>>
    | ((views: Views<UIViewDefinition>) => void);
  viewType: ViewType;
  transformer: ViewDefinitionTransformer<APIViewDefinition, UIViewDefinition>;
}
interface Result<T> {
  onViewCreate: (view: CreatableView<T>) => Promise<View<T>>;
  onViewUpdate: (view: View<T>) => Promise<View<T>>;
  onViewDelete: (view: View<T>) => Promise<View<T>>;
}

export const useViewActions = <
  VT extends ViewTypeEnum,
  UIViewDefinition,
  APIViewDefinition,
>({
  views,
  setViews,
  viewType,
  transformer,
}: Props<
  VT,
  UIViewDefinition,
  APIViewDefinition
>): Result<UIViewDefinition> => {
  const viewTransformer = useMemo(
    () => getViewTranformer(transformer),
    [transformer],
  );

  const onViewCreate = useCallback(
    (view: CreatableView<UIViewDefinition>) => {
      return ViewApI.saveView<APIViewDefinition>(
        viewTransformer.to({ ...view, viewType, createdBy: -1, viewId: -1 }),
      ).then(
        (v: View<APIViewDefinition>) => {
          const tranformed = viewTransformer.from(v);

          setViews(created(tranformed)(views));

          return tranformed;
        },
        (error) => {
          throw error;
        },
      );
    },
    [setViews, viewTransformer, viewType, views],
  );
  const onViewUpdate = useCallback(
    (view: View<UIViewDefinition>) => {
      setViews(updating(view)(views));

      return ViewApI.updateView<APIViewDefinition>(
        viewTransformer.to(view),
      ).then(
        (v: View<APIViewDefinition>) => {
          const transformedView = viewTransformer.from(v);

          setViews(updated(transformedView)(views));

          return transformedView;
        },
        (error) => {
          setViews(reverted(view)(views));

          throw error;
        },
      );
    },
    [setViews, viewTransformer, views],
  );
  const onViewDelete = useCallback(
    (view: View<UIViewDefinition>) => {
      setViews(deleting(view)(views));

      return ViewApI.deleteView<APIViewDefinition>(
        viewTransformer.to(view),
      ).then(
        () => {
          setViews(deleted(view)(views));

          return view;
        },
        (error) => {
          setViews(undeleted(view)(views));

          throw error;
        },
      );
    },
    [setViews, viewTransformer, views],
  );

  return {
    onViewCreate,
    onViewDelete,
    onViewUpdate,
  };
};
